/**
 * @file BaseRowComponent.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import { Logger } from "../../../logger/Logger";
import { container } from "../../../di/inversify.config";
import { TYPES } from "../../../logger/Types";
import { StorageKey } from "../../login/utils/StorageKey";
const { v4: uuidv4 } = require('uuid'); // eslint-disable-line
var CryptoJS = require("crypto-js");  // eslint-disable-line

 /**
 * A class definition for BaseRowComponent
 */
export abstract class BaseRowComponent<BaseProps, State> extends React.Component<BaseProps, State> {

    protected logger: Logger = container.get<Logger>(TYPES.LoggerService);
    private secretKey = process.env.REACT_APP_SECRET_KEY;

    TypographyStyle = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: '1',
        WebkitBoxOrient: 'vertical',
        fontSize:"0.9em"
    };

    index = 0;
    page = 0;
    rowsPerPage = 0;

    constructor(props: BaseProps) {
        super(props);
        this.page = 0,
        this.rowsPerPage = 10
    }

    // Avoid a layout jump when reaching the last page with empty rows.
    emptyRows = 0;

    protected handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        this.index = 0;
        this.setPage(newPage);
        this.logger.verbose(`handleChangePage - ${this.index} & ${newPage}`, BaseRowComponent.name);
        this.loadMoreData();
    };

    protected setPage = (newPage: number) => {
        this.page = newPage;
        this.shareStates();
    }

    protected setRowsPerPage = (newValue: number) => {
        this.rowsPerPage = newValue;
        this.shareStates();
        this.logger.verbose(`setRowsPerPage - ${this.rowsPerPage}`, BaseRowComponent.name);

    }

    protected abstract shareStates();

    protected abstract loadMoreData();

    protected handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        this.index = 0;
        this.setRowsPerPage(parseInt(event.target.value, 10));
        this.setPage(0);
    };

    protected isEmpty(str) {
        return (!str || str.length === 0);
    }

    protected getFormattedDate = (date?: string) => {
        const isoDate = new Date(date!!); // eslint-disable-line
        const dateFormatted = new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
          }).format(isoDate);
          
        return dateFormatted;
    }

    protected getRowBackground = ()=>  this.index % 2 == 0 ? '#F4F5F6': '#FFFFFF' 

    protected getRowRandomID = ()=> uuidv4();

    /**
     * Gets current user details
     * @returns 
     */
    protected getCurrentUser = () => this.getValueFromSessionStorage(StorageKey.CURRENT_USER);
        
    /**
     * Gets Access token
     * @returns AccessToken
     */
    // eslint-disable-next-line
    protected getSesionToken = () => !this.isEmpty(this.getValueFromSessionStorage(StorageKey.APP_SESSION_KEY)) ? JSON.parse(this.getValueFromSessionStorage(StorageKey.APP_SESSION_KEY)!!) : '';
    
    /**
     * Validates and get session
     */
    protected validateAndGetSession (name: string) : any | undefined {
        const sessionToken = this.getSesionToken();
        if(this.isEmpty(sessionToken)) {
            this.logger.warning("Empty access token", name);
            return undefined;
        }
        if(!Object.keys(sessionToken).includes("accessToken") || sessionToken.accessToken.length <= 0) {
            this.logger.warning("loadPatients()- Empty access token", name);
            return undefined;
        }

        return sessionToken.accessToken;
    }

    /**
     * Gets logged in user role
     * @returns RoleType
     */
    protected getLoggedInUserRole = () => this.getValueFromSessionStorage(StorageKey.ROLE_TYPE);
        
    /**
     * Get generic value from sessionStorage
     * @param storage StorageKey
     * @returns 
     */
    private getValueFromSessionStorage = (storage: StorageKey) => {
        const encryptedText = window.localStorage.getItem(storage);
        if(!encryptedText){
            return '';
        }
        this.logger.warning(`DeCrypting.... ${encryptedText}`, BaseRowComponent.name); 
        const decryptedText = CryptoJS.AES.decrypt(encryptedText, this.secretKey).toString(CryptoJS.enc.Utf8);
        this.logger.warning(`decrypted -${storage} - ${decryptedText}`, BaseRowComponent.name);
        return decryptedText;
    }

}